import { DeployEnv, Site, SLD } from '@patsnap-utils/biz';

/**
 * LLM 模型选择工具（只读）
 *
 * 模型由 rd-home-sdk（@eureka/rd-sdk-plugin）的模型切换组件写入 localStorage，
 * company-scout 作为内嵌 agent 仅在请求底层读取并注入 header，不写入。
 *
 * 存储约定必须与 rd-home-sdk 的 home-plugin-sdk/src/utils/llm-model.ts 保持一致。
 */

export const DEFAULT_LLM_MODEL = 'Auto';

export const LLM_MODEL_STORAGE_PREFIX = 'rd-home-sdk:llm-model';

/**
 * 各内嵌 agent 对应的 functionId（写死，与 rd-home-sdk 的 FunctionId 枚举一致）。
 * 用作读取 localStorage 缓存的 key 后缀。
 */
export const LLM_FUNCTION_ID = {
    COMPANY_SEARCH: 'rd_insights_company_search',
} as const;

export function isLlmModelEnabled(): boolean {
    try {
        const env = new Site({ sld: SLD.EUREKA }).env;
        return env !== DeployEnv.US && env !== DeployEnv.EU;
    } catch {
        return true;
    }
}

export function getLlmModelStorageKey(functionId: string): string {
    return `${LLM_MODEL_STORAGE_PREFIX}:${functionId}`;
}

export function getStoredLlmModel(functionId?: string): string {
    if (!functionId || typeof localStorage === 'undefined') return DEFAULT_LLM_MODEL;
    try {
        return localStorage.getItem(getLlmModelStorageKey(functionId)) || DEFAULT_LLM_MODEL;
    } catch {
        return DEFAULT_LLM_MODEL;
    }
}

/**
 * 生成请求 header，Auto 或未启用时返回空对象（由后端使用默认模型）。
 * @param functionId 内嵌 agent 写死的 functionId，如 LLM_FUNCTION_ID.COMPANY_SEARCH
 */
export function getLlmModelHeader(functionId?: string): Record<string, string> {
    if (!isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === DEFAULT_LLM_MODEL ? {} : { 'X-Select-Model': model };
}

/**
 * 从当前页面 URL 读取 function_id（agent-flow 页面通过 query 传入）。
 * 兼容 history 模式（location.search）与 hash 模式（hash 中的 query）。
 * URL 上没有时返回 undefined。
 */
export function getFunctionIdFromUrl(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        const search = window.location.search;
        const fromSearch = new URLSearchParams(search).get('function_id');
        if (fromSearch) return fromSearch;
        const hash = window.location.hash;
        const queryIndex = hash.indexOf('?');
        if (queryIndex === -1) return undefined;
        return new URLSearchParams(hash.slice(queryIndex)).get('function_id') || undefined;
    } catch {
        return undefined;
    }
}

/**
 * 当 URL 带有 function_id 时，取缓存中对应模型生成 header；否则返回空对象。
 */
export function getUrlLlmModelHeader(): Record<string, string> {
    return getLlmModelHeader(getFunctionIdFromUrl());
}

/**
 * 生成请求 body 的 model 参数，Auto 或未启用时返回空对象（由后端使用默认模型）。
 * 部分接口要求模型值在 body 而非 header 传递。
 * @param functionId 业务项目的 functionId
 */
export function getLlmModelParam(functionId?: string): { model?: string } {
    if (!isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === DEFAULT_LLM_MODEL ? {} : { model };
}

/**
 * 当 URL 带有 function_id 时，取缓存中对应模型生成 body 的 model 参数；否则返回空对象。
 */
export function getUrlLlmModelParam(): { model?: string } {
    return getLlmModelParam(getFunctionIdFromUrl());
}
